import { endOfDay, isDate } from "date-fns";
import * as Yup from "yup";
import { validarCuilCuit } from "../../../../../util/cuilcuit";
import { dateIsBefore, isDecimal } from "../../../../../util/validacionesYup";
import { validacionCalle, validacionCodigoPostal, validacionDepartamento, validacionLocalidad, validacionNumeroCalle, validacionPais, validacionPiso, validacionProvincia } from '../../../../commons/yup/ValidacionesLocalidades';

const Decimal8Digits2Decimals = () => {
  return Yup.string()
    .matches(/^[0-9]+(,[0-9]+)?$/, "Solo ingrese comas y números")
    .test({
      name: "cantidadDigitosDecimales",
      message: "Máximo 2 dígitos después de la coma",
      test: (value) => isDecimal(value),
    })
    .test({
      name: "cantidadTotalDigitos",
      message: "Máximo 8 dígitos enteros",
      test: function (value) {
        if (value) {
          return value?.split(",")[0]?.length <= 8;
        } else {
          return true;
        }
      },
    })
}


export const sustanciasForm1Validation = (sustanciasAgregadas, sustancia) => Yup.object({
  nombre: Yup.string()
    .required("Campo obligatorio")
    .max(50, "Máximo 50 caracteres"),
  sustancia: Yup.string().typeError("Campo obligatorio").required("Campo obligatorio").nullable().test({
    name: "sinSustanciasRepetidas",
    message: "No se pueden agregar sustancias repetidas",
    test: function (value) {
      if (value && sustanciasAgregadas) {
        if (sustancia?.idTabla !== null && sustancia?.idTabla !== undefined) {
          return !sustanciasAgregadas.find((s, index) => 
            s.sustancia.sustancia.toString() === value && index !== sustancia.idTabla
          );
        } else {
          return !sustanciasAgregadas.find((s) => 
            s.sustancia.sustancia.toString() === value
          );
        }
      }
      return true;
    },
  }),
  cantidad: Yup.string()
    .required("Campo obligatorio")
    .matches(/^[0-9]+(,[0-9]+)?$/, "Solo ingrese comas y números")
    .test({
      name: "valoresAceptados",
      message: "Solo acepta valores contenidos entre 0 y 1000000",
      test: function (value) {
        return value?.match(/^\d{1,6}(,\d{1,3})?$/) && value !== '0';
      },
    }),
  unidad: Yup.string().required("Campo obligatorio"),
});

export const validateNewEquipoRadiologico = Yup.object({
  marca: Yup.string()
    .required("La marca es requerida")
    .max(100, "La marca no puede exceder los 100 caracteres")
    .nullable(),

  modelo: Yup.string()
    .required("El modelo es requerido")
    .max(100, "El modelo no puede exceder los 100 caracteres")
    .nullable(),

  idTipoEquipoRadiologico: Yup.string().required("El tipo es requerido").nullable(),

  idCaracteristicaEquipoRadiologico: Yup.string()
    .required("La característica es requerida")
    .nullable(),

  idTipoRadiacion: Yup.string()
    .required("El tipo de radiación es requerido")
    .nullable(),

  actividad: Yup.string()
    .required("La actividad es requerida")
    .max(255, "La actividad no puede exceder los 255 caracteres")
    .nullable(),

  radioisotipoFuente: Yup.string()
    .required("El radioisotipo fuente es requerido")
    .max(100, "El radioisotipo fuente no puede exceder los 100 caracteres")
    .nullable(),

  idTipoFuente: Yup.string()
    .required("El tipo de fuente es requerido")
    .nullable(),

  idTipoMetodoProcesamientoImagen: Yup.string().required("El Método de Procesamiento es requerido").nullable(),

  potenciaEquipoKVP: Decimal8Digits2Decimals()
    .required("La potencia KVP es requerida")
    .nullable(),

  potenciaEquipomAp: Decimal8Digits2Decimals()
    .required("La potencia mAp es requerida")
    .nullable(),

  potenciaEquipoMeV: Decimal8Digits2Decimals()
    .required("La potencia MeV es requerida")
    .nullable(),

  procesamientoImagenes: Yup.boolean().nullable(),

  observaciones: Yup.string()
    .required("Las observaciones son requeridas")
    .max(200, "Las observaciones no pueden exceder los 200 caracteres")
    .nullable(),
});



export const validateDosimetriaForm = Yup.object({
  tipoDosimetria: Yup.string().nullable().typeError("El Tipo de Dosimetria es requerido").required("El Tipo de Dosimetria es requerido"),
  metodoDosimetria: Yup.string().required('El Método de Dosimetria es requerido').nullable(),
  cantidadPersonal: Yup.number().required('La Cantidad de Personal es requerida').min(0, "La Cantidad de Personal debe ser un número positivo").max(2147483647, "La Cantidad de Personal no puede ser mayor a 2.147.483.647").typeError("La Cantidad de Personal debe ser un número entero"),
  cuitResponsable: Yup.string().max(11, "El CUIT debe tener como máximo 11 caracteres").required("El CUIT es requerido").test({
    name: "cuitValido",
    message: "Debe ingresar un CUIT válido",
    test: function (value) {
      if (value) {
        return validarCuilCuit(value, true, true);
      }
      return true;
    },
  }),
});


const validateFechaExpedicion = Yup.date()
  .nullable()
  .required("La Fecha de Expedición es requerida")
  .typeError('Fecha inválida')
  .test({
    name: "fechaMaximaMañana",
    message: "La Fecha de Expedición maxima es la fecha actual",
    test: (value, testContext) => {
      if (value) {
        return dateIsBefore(value, endOfDay(new Date()), true);
      }
      return true;
    },
  })
  .test({
    name: "fechaMenorAFechaDeVencimiento",
    message:
      "La Fecha de Expedición debe ser menor a la Fecha de Vencimiento",
    test: function (value, testContext) {
      if (isDate(testContext.parent.fechaVencimiento)) {
        if (value) {
          return dateIsBefore(
            value,
            testContext.parent.fechaVencimiento,
            true,
          );
        }
      }
      return true;
    },
  });

const validateFechaVencimiento = Yup.date()
  .nullable()
  .required("La Fecha de Vencimiento es requerida")
  .typeError('Fecha inválida')
  .test({
    name: "fechaMenorAFechaDeVencimiento",
    message:
      "La Fecha de Vencimiento debe ser mayor a la Fecha de Expedición",
    test: function (value, testContext) {
      if (isDate(testContext.parent.fechaExpedicion)) {
        if (value) {
          return dateIsBefore(
            testContext.parent.fechaExpedicion,
            value,
            true,
          );
        }
      }
      return true;
    },
  });

export const validateResponsableUsoForm = Yup.object({
  cuitResponsable: Yup.string().max(11, "El CUIL debe tener como máximo 11 caracteres").required("El CUIL es requerido").test({
    name: "cuitValido",
    message: "Debe ingresar un CUIL válido",
    test: function (value) {
      if (value) {
        return validarCuilCuit(value, false, true);
      }
      return true;
    },
  }),
  numeroAutorizacionIndividual: Yup.number().required('El Número de Autorización Individual es requerido')
    .typeError("El Nro Autorización Individual debe ser un número entero")
    .integer("El Nro Autorización Individual ser un número entero")
    .min(0, "El Nro Autorización Individual debe ser positivo")
    .max(2147483647, "El Nro Autorización Individual no puede ser mayor a 2.147.483.647"),
  numeroPermisoHabilitante: Yup.number()
    .required('El Número de Permiso Habilitante es requerido')
    .integer()
    .typeError("El Nro Permiso Habilitante debe ser un número entero")
    .integer("El Nro Permiso Habilitante ser un número entero")
    .min(0, "El Nro Permiso Habilitante debe ser positivo")
    .max(2147483647, "El Nro Permiso Habilitante no puede ser mayor a 2.147.483.647"),
  matriculaProfesional: Yup.string()
    .max(20, "La Matricula Profesional debe tener como máximo 11 caracteres")
    .required("La Matricula Profesional es requerida"),
  fechaExpedicion: validateFechaExpedicion,
  fechaVencimiento: validateFechaVencimiento,
});


export const validateResponsableInstalacionForm = Yup.object({
  cuitResponsable: Yup.string().max(11, "El CUIL debe tener como máximo 11 caracteres").required("El CUIL es requerido").test({
    name: "cuitValido",
    message: "Debe ingresar un CUIL válido",
    test: function (value) {
      if (value) {
        return validarCuilCuit(value, false, true);
      }
      return true;
    },
  }),
  numeroLicenciaHabilitante: Yup.number()
    .required('El Número de Licencia Habilitante es requerido')
    .typeError("El Nro Licencia Habilitante debe ser un número entero")
    .integer("El Nro Licencia Habilitante debe ser un número entero")
    .min(0, "El Nro Licencia Habilitante debe ser positivo")
    .max(2147483647, "El Nro Licencia Habilitante no puede ser mayor a 2.147.483.647"),
  domicilioHabilitacion: Yup.string().max(50, "El Domicilio de Habilitacion debe tener como máximo 50 caracteres"),
  fechaExpedicion: validateFechaExpedicion,
  fechaVencimiento: validateFechaVencimiento,
});


export const validateResponsableProteccionRadiologica = Yup.object({
  cuitResponsable: Yup.string().max(11, "El CUIL debe tener como máximo 11 caracteres").required("El CUIL es requerido").test({
    name: "cuitValido",
    message: "Debe ingresar un CUIL válido",
    test: function (value) {
      if (value) {
        return validarCuilCuit(value, false, true);
      }
      return true;
    },
  }),
  matriculaProfesional: Yup.string().max(20, "La Matricula Profesional debe tener como máximo 20 caracteres"),
  tituloProfesional: Yup.string().max(20, "El Título Profesional debe tener como máximo 20 caracteres"),
  numeroHabilitacion: Yup.number()
    .required('El Nro de Habilitacion es requerido')
    .typeError("El Nro de Habilitacion debe ser un número entero")
    .integer("El Nro de Habilitacion ser un número entero")
    .min(0, "El Nro de Habilitacion debe ser positivo")
    .max(2147483647, "El Nro de Habilitacion no puede ser mayor a 2.147.483.647"),
});


export const validateAddEquipoRadiologico = Yup.object({
  equipoRadiologico: Yup.string().required("Equipo radiologico es requerido").nullable(),
})


export const ValidateProveedor = (proveedores, id) => Yup.object({
  razonSocial: Yup.string().max(50, "Razon Social debe tener como máximo 50 caracteres").required("Campo obligatorio"),
  cuit: Yup.string().max(11, "El CUIT debe tener como máximo 11 caracteres").required("Campo obligatorio")
    .test({
      name: "cuitValido",
      message: "Debe ingresar un CUIT válido",
      test: function (value) {
        if (value) {
          return validarCuilCuit(value, true, true);
        }
        return true;
      }
    })
    .test({
      name: "cuitNoRepetido",
      message: "Este CUIT ya fue agregado como proveedor/comprador",
      test: function (value, testContext) {

        if (value) {
          // Caso edicion
          if (id !== null && id !== undefined) {
            const idMismoCuit = proveedores.findIndex((p) => p.cuit === value && p.esComprador === testContext.parent.esComprador);

            return idMismoCuit === id || idMismoCuit === -1;
          } else {
            // Caso agregar
            if (proveedores.find((p) => p.cuit === value && p.esComprador === testContext.parent.esComprador)) {
              return false;
            }
          }

          return true;
        }
      }
    }),
  esComprador: Yup.boolean(),
  pais: validacionPais.required("Campo obligatorio"),
  provincia: validacionProvincia.required("Campo obligatorio"),
  localidad: validacionLocalidad.required("Campo obligatorio"),
  calle: validacionCalle.required("Campo obligatorio"),
  numero: validacionNumeroCalle("km"),
  departamento: validacionDepartamento,
  piso: validacionPiso,
  codigoPostal: validacionCodigoPostal,
  entreCalle1: validacionCalle,
  entreCalle2: validacionCalle,

})

export const ValidateEstudiosAmbientales = (estudiosAgregadosLocalmente, id) => (
  Yup.object({
    idTipoEstudio: Yup.string().nullable().required("Tipo de Estudio es requerido"),
    numeroFrecuencia: Yup.number()
      .required("Frecuencia de Estudio es requerida")
      .typeError("Frecuencia de Estudio debe ser un número entero")
      .integer("Frecuencia de Estudio debe ser un número entero")
      .positive("Frecuencia de Estudio debe ser positivo")
      .max(2147483647, "La frecuencia no puede ser mayor a 2.147.483.647"),
    frecuenciaEstudio: Yup.string().nullable().required("Frecuencia de Estudio es requerida"),
    idMetodologiaEmpleada: Yup.string().nullable().required("Metodologá Empleada es requerida")
      .test({
        name: "sinDuplicados",
        message: "No se pueden agregar metologías repetidas",
        test: function (value, testContext) {
          if (value && estudiosAgregadosLocalmente) {
            return !estudiosAgregadosLocalmente.find((estudio) => {
              return estudio.idTipoEstudio.toString() === testContext.parent.idTipoEstudio
                && estudio.idMetodologiaEmpleada.toString() === value && id !== estudio?.index
            });
          }
          return true;
        }
      }),
  })
);
export const ValidateMetodologiaEmpleada = (metodologiasEmpleadas) => (
  Yup.object({
    descripcion: Yup.string()
      .max(50, "La metodología no puede exceder los 50 caracteres")
      .required("Metodología es requerida")
      .test({
        name: "SinMetodologiasRepetidas",
        message: "La metodología ya está registrada en el sistema",
        test: function (value) {
          if (value && metodologiasEmpleadas) {
            return !metodologiasEmpleadas.find((metodologia) => {
              return metodologia.nombre.toLowerCase() === value.toLowerCase()
            })
          }
          return true;
        }
      }),
  })
)

export const ValidateUsos = Yup.object({
  usos: Yup.string().nullable().required("Origen/Usos es requerido"),
  modoEmpleo: Yup.string().nullable().required("Modo de Empleo es requerido"),
})